import React, { useRef } from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import NoDataFound from '../../NoDataFound';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const MOBILE_BREAKPOINT = 768;

const InstitutePopularPrograms = ({ programs }) => {
  const swiperRef = useRef(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  // Check if programs is null, undefined, or empty array
  const isDataEmpty = !programs || programs.length === 0;

  // Show "Data not available" message when programs is empty
  if (isDataEmpty) {
    return (
      <NoDataFound message='Data not available' height='100%'/>
    );
  }


  return (
    <StyledWrapper>
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        spaceBetween={30}
        slidesPerView={1}
        // navigation={true}
        pagination={{
          clickable: true,
          dynamicBullets: true,
        }}
        autoplay={{
          delay: 3000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        ref={swiperRef}
        loop={true}
        breakpoints={{
          0: {
            slidesPerView: 1,
            spaceBetween: 30,
          },
          [MOBILE_BREAKPOINT]: {
            slidesPerView: 2,
            spaceBetween: 30,
          },
          1200: {
            slidesPerView: 3,
            spaceBetween: 30,
          },
          1600: {
            slidesPerView: 4,
            spaceBetween: 30,
          },
        }}
        onInit={(swiper) => {
          swiperRef.current = swiper;
          swiper.params.navigation.prevEl = prevRef.current;
          swiper.params.navigation.nextEl = nextRef.current;
          swiper.navigation.init();
          swiper.navigation.update();
        }}
        navigation={{
          prevEl: prevRef.current,
          nextEl: nextRef.current,
        }}
        className="programs-swiper"
      >
        {programs.map((program) => (
          <SwiperSlide key={program.id}>
            <div className="card">
              {/* Background SVG */}
              <div className="BG">
                <img src="/assets/images/image.svg" alt="Background icon" className="ionicon" />
              </div>
              
              {/* Program Content */}
              <div className="content">
                <p className="heading">{program.name}</p>
                <div className="enrollment-stats">
                  <p className="enrollment-count">{program.enrollments}</p>
                  <p className="enrollment-label">Total Enrollments</p>
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  .programs-swiper {
    padding: 20px 0 50px 0;
  }

  .programs-swiper .swiper-slide {
    height: auto;
    display: flex;
  }

  .card {
    width: 100%;
    height: 200px;
    position: relative;
    background-color:rgba(227, 231, 253, 0.87);
    border-bottom: 3px solid #4c6bff;
    overflow: hidden;
    -webkit-box-shadow: 0px 12px 65px -39px rgba(0, 0, 0, 1);
    -moz-box-shadow: 0px 12px 65px -39px rgba(0, 0, 0, 1);
    box-shadow: 0px 12px 65px -39px rgba(0, 0, 0, 1);
    border-radius: 8px;
    transition: all 0.3s ease;
    cursor: pointer;
  }

  .BG {
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .BG .ionicon {
    position: absolute;
    width: 40%;
    left: -10%;
    top: -20%;
    opacity: ;
    transition: all 0.5s;
  }

  .badge {
    position: absolute;
    top: 10px;
    right: 10px;
    background-color: #ffc53a;
    color: black;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 0.7rem;
    font-weight: 600;
    z-index: 10;
  }

  .content {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    color: rgb(30, 30, 30);
    gap: 12px;
  }

  .heading {
    font-size: 1.2em;
    font-weight: 700;
    color: rgb(30, 30, 30);
    text-align: center;
    line-height: 1.2;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    max-height: 2.4em;
    margin: 0;
  }

  .enrollment-stats {
    text-align: center;
    margin-top: 8px;
  }

  .enrollment-count {
    font-size: 2rem;
    font-weight: bold;
    color: #36348E;
    margin: 0;
    line-height: 1;
  }

  .enrollment-label {
    font-size: 0.95em;
    color: rgb(128, 128, 128);
    margin: 2px 0 0 0;
  }

  .card-btn {
    margin-top: 15px;
    height: 32px;
    width: 80%;
    border: none;
    background: linear-gradient(60deg, #4c6bff, #8196ff);
    color: white;
    border-radius: 20px;
    cursor: pointer;
    font-size: 0.8em;
    font-weight: 600;
    transition: all 0.3s ease;
  }

  .card-btn:hover {
    background: linear-gradient(60deg, #3b5bff, #6b85ff);
    transform: translateY(-2px);
  }

  .card:hover .BG .ionicon {
    left: 0%;
    top: 0%;
    transform: rotate(180deg) scale(9);
    opacity: 0.5;
  }

  .card:hover {
    transform: translateY(-5px);
    box-shadow: 0px 20px 80px -20px rgba(0, 0, 0, 0.3);
  }

  .card:hover .badge {
    background-color: #fff;
    color: #4c6bff;
  }

  /* Swiper Navigation Styles */
  .programs-swiper .swiper-button-next,
  .programs-swiper .swiper-button-prev {
    color: #4c6bff;
    background: white;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transition: all 0.3s ease;
    top: 40%;
  }

  .programs-swiper .swiper-button-next:after,
  .programs-swiper .swiper-button-prev:after {
    font-size: 18px;
    font-weight: bold;
  }

  .programs-swiper .swiper-button-next:hover,
  .programs-swiper .swiper-button-prev:hover {
    background: #4c6bff;
    color: white;
    transform: scale(1.1);
  }

  .programs-swiper .swiper-button-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Swiper Pagination Styles */
  .programs-swiper .swiper-pagination {
    bottom: 10px;
  }

  .programs-swiper .swiper-pagination-bullet {
    background: #ddd;
    opacity: 1;
    width: 12px;
    height: 12px;
    margin: 0 6px;
    transition: all 0.3s ease;
  }

  .programs-swiper .swiper-pagination-bullet-active {
    background: #4c6bff;
    transform: scale(1.2);
  }

  .programs-swiper .swiper-pagination-bullet:hover {
    background: #6b85ff;
    transform: scale(1.1);
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .card {
      height: 280px;
    }
    
    .enrollment-count {
      font-size: 1.7rem;
    }
    
    .heading {
      font-size: 1rem;
    }

    .programs-swiper {
      padding: 15px 0 40px 0;
    }

    .programs-swiper .swiper-button-next,
    .programs-swiper .swiper-button-prev {
      width: 36px;
      height: 36px;
    }

    .programs-swiper .swiper-button-next:after,
    .programs-swiper .swiper-button-prev:after {
      font-size: 14px;
    }
  }

  @media (max-width: 480px) {
    .programs-swiper .swiper-button-next,
    .programs-swiper .swiper-button-prev {
      display: none;
    }
  }
`;

export default InstitutePopularPrograms;